import './screens.css';
import { el, setText, text } from './walkout.js';
import { heatWords } from './heatWords.js';
import { weaponMoves } from '../core/weapons.js';
import { createPromoter } from './promoter.js';
import baseMoves from '../core/moves.json';
import { needs } from './contract.js';
import { changeWords } from './venueWords.js';

/**
 * The aftermath: what just happened, and what it cost.
 *
 * Four things, in the order somebody wants them: who won, how it ended, what
 * the crowd saw while it was happening, and where that leaves the player on the
 * ladder. The last one is the reason the screen exists — a rank that moved
 * during a round nobody was watching is a rank the player never sees move.
 *
 * It reads the record the campaign already keeps rather than being told
 * separately: `campaign.onRecord` fires with the fight, the ladder movement and
 * the gate, and everything on this screen comes out of that one event.
 */

/** Every move there is, so a finish can be named rather than keyed. */
const MOVES = Object.freeze({ ...baseMoves.moves, ...weaponMoves() });

/**
 * How it ended, in words.
 *
 * `finishedBy` is a move **key** (gotcha 38) and the display name is a lookup,
 * not the same field. `null` is the attrition finish — nothing landed, the body
 * simply gave out — which is a real way to lose and deserves saying so.
 */
export function howItEnded({ by = 'knockout', method = null, on = null } = {}, nameOf = null) {
  // A decision has no move behind it and must not be read as one. Before the
  // round clock existed, `method: null` could only mean the attrition finish;
  // now it means one of two quite different things and the verdict is what
  // tells them apart.
  if (by === 'draw') return on ? `a draw on ${on}` : 'a draw — nothing in it';
  if (by === 'decision') return on ? `a decision on ${on}` : 'a decision';
  if (!method) return 'the body gave out';
  if (MOVES[method]?.name) return `a ${MOVES[method].name.toLowerCase()}`;
  // Not a move: part of the room. The arena knows what it is called.
  return nameOf?.(method) ?? method;
}

export function createAftermath({ campaign, match, container = document.body, nameOf = null }) {
  needs(campaign, ['onRecord', 'actComplete', 'ladder'], {
    who: 'the aftermath',
    what: 'campaign',
  });
  needs(campaign.ladder, ['size'], { who: 'the aftermath', what: 'campaign.ladder' });
  needs(match, ['reset'], { who: 'the aftermath', what: 'match' });
  const root = el('div', 'screen');
  root.hidden = true;

  const kind = text('div', 'screen__kind', '');
  const verdict = text('div', 'screen__verdict', '');
  const how = text('div', 'screen__how', '');
  const moved = text('div', 'screen__moved', '');
  // The room it was fought in, and whether it changed hands.
  const room = text('div', 'screen__room', '');
  root.append(kind, verdict, how, moved, room);

  // And the man who booked it, with a view on how it went. After the movement,
  // because that is the thing he is having a view *about*.
  const sala = createPromoter({ campaign, screen: 'aftermath' });
  root.append(sala.element);

  const heat = el('div', 'screen__heat');
  root.append(heat);

  const standing = el('div', 'screen__standing');
  const place = el('div');
  const toGo = el('div');
  standing.append(place, toGo);
  root.append(standing);

  const go = el('button', 'screen__go');
  go.textContent = 'next fight';
  root.append(go);
  root.append(text('div', 'screen__hint', 'enter, space or click'));

  container.append(root);

  let up = false;

  /** Draws one recorded fight. Fed by `campaign.onRecord`. */
  function show(event) {
    if (!event) return false;

    root.dataset.won = String(event.won);
    root.dataset.drawn = String(Boolean(event.drawn));
    root.dataset.act = String(event.act);
    setText(kind, event.act ? 'for the card' : 'circuit fight');
    setText(
      verdict,
      event.drawn
        ? `You and ${event.name} go the distance`
        : event.won
          ? `You beat ${event.name}`
          : `${event.name} beat you`
    );
    setText(how, `by ${howItEnded(event, nameOf)}`);

    // The movement, in the terms the ladder moved in: a win that took a place
    // and a win that only cost the loser one are different results, and the
    // number saying so is the whole reason to show a rank at all.
    const places = event.from - event.rank;
    setText(
      moved,
      places > 0
        ? `rank ${event.from} → ${event.rank}  (up ${places})`
        : places < 0
          ? `rank ${event.from} → ${event.rank}  (down ${-places})`
          : event.drawn
            ? `rank ${event.rank}, and nobody moved`
            : `rank ${event.rank}, unmoved`
    );

    setText(room, changeWords(event.venue));
    room.hidden = !event.venue;
    room.dataset.changed = event.venue?.changed ?? 'none';

    // Handed the fight itself rather than left to find it: the history has it
    // too, but this screen is *about* this result and a promoter reading the
    // wrong one is the sort of bug that only shows up on the second fight.
    sala.update(event);

    // What the crowd saw. Captured before the round reset wiped it.
    const events = event.heat ?? [];
    heat.replaceChildren(
      ...events.map((entry) => {
        const row = el('div', 'line');
        row.dataset.rise = String(entry.rise);
        const sign = entry.delta > 0 ? '+' : '';
        row.textContent = `${sign}${Math.round(entry.delta)}  ${heatWords(entry, { who: event.name })}`;
        return row;
      })
    );
    if (!events.length) {
      heat.append(text('div', 'line', 'nothing the crowd will remember'));
    }

    const gate = event.gate;
    setText(place, `rank ${gate.rank} of ${campaign.ladder.size}`);
    setText(
      toGo,
      gate.open
        ? campaign.actComplete
          ? 'the card is yours'
          : 'contender — the card is open'
        : `${gate.short} place${gate.short === 1 ? '' : 's'} off the card`
    );
    root.dataset.contender = String(gate.open);

    root.hidden = false;
    up = true;
    return true;
  }

  function dismiss() {
    if (!up) return false;
    up = false;
    root.hidden = true;
    // Back to the marks, which is what fires the next walkout.
    match.reset();
    return true;
  }

  function onKey(event) {
    if (!up) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    dismiss();
  }

  go.addEventListener('click', dismiss);
  window.addEventListener('keydown', onKey);
  const stop = campaign.onRecord(show);

  return {
    element: root,
    get up() {
      return up;
    },
    show,
    dismiss,
    dispose() {
      stop?.();
      window.removeEventListener('keydown', onKey);
      root.remove();
    },
  };
}
